import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { HorizontalPaddingLayout } from '@/layouts/HorizontalPadding';
import { Divider } from '@/components/Divider';
import { HorizontalTab, HorizontalTabs } from '@/components/HorizontalTab';
import { FundActions, FundCover } from './components';
import { useFlag } from '@/unleash';
import { useSetBackUrl } from '@/hooks/backUrl';
import { useScrollToTopOnMount } from '@/hooks/scroll';
import { useGetFundLandingData } from '../../containers/LandingSection/hooks/useGetFundLandingData';
import { useCheckHasAssetActivities } from '../../hooks/useCheckHasAssetActivities';
import { useFindFundByQuery } from '@/modules/invest/subModules/fund/hooks/useFindFund';
import { useGetUserStatus } from '@/userStatus/hooks';
import {
  AssetSection,
  ConsultationSection,
  IntroductionSection,
  LandingSection,
} from '../../containers';

const fundSections = [
  'asset',
  'introduction',
  'consultation',
  'landing',
] as const;

type TFundSection = (typeof fundSections)[number];

// Type guard to check if a string is a valid TFundSection
const isFundSection = (arg: string): arg is TFundSection => {
  return (fundSections as readonly string[]).includes(arg);
};

export const SingleFundSections: FC = () => {
  useSetBackUrl('/invest/fund');
  useScrollToTopOnMount();
  const { isVerified } = useGetUserStatus();
  const { isEnabled: isAISummerizeEnabled } = useFlag('web.fund.aiSummerize');
  const { fund } = useFindFundByQuery();
  const { hasAssetActivities } = useCheckHasAssetActivities();
  const { query, replace } = useRouter();
  const { fundLandingData } = useGetFundLandingData();

  const queryType = Array.isArray(query.type) ? query.type[0] : query.type;

  const shouldShowAiAnalysis = isAISummerizeEnabled && fund?.showAiData;

  const [section, setSection] = useState<TFundSection>(() => {
    if (queryType && isFundSection(queryType)) {
      if (queryType === 'landing' && !fundLandingData) {
        return isVerified && hasAssetActivities ? 'asset' : 'introduction';
      }
      return queryType === 'asset' && !hasAssetActivities
        ? 'introduction'
        : queryType;
    }
    return isVerified && hasAssetActivities ? 'asset' : 'introduction';
  });

  const handleChangeSection = (value: TFundSection) => {
    replace({ query: { ...query, type: value } });
    setSection(value);
  };

  return (
    <div className="max-md:-mb-4">
      <FundCover />
      <HorizontalPaddingLayout>
        <HorizontalTabs selectedValue={section} onChange={handleChangeSection}>
          <HorizontalTab
            analytics={['FUND_CLICK_ASSET_TAB']}
            isVisible={isVerified && hasAssetActivities}
            value="asset"
          >
            دارایی
          </HorizontalTab>
          <HorizontalTab
            analytics={['FUND_CLICK_INTRODUCTION_TAB']}
            value="introduction"
          >
            معرفی صندوق
          </HorizontalTab>
          <HorizontalTab
            analytics={
              shouldShowAiAnalysis
                ? ['FUND_CLICK_AI_TAB']
                : ['FUND_CLICK_CONSULTATION_TAB']
            }
            className="whitespace-nowrap"
            value="consultation"
          >
            {shouldShowAiAnalysis ? 'هوش‌ مصنوعی' : 'راهنمایی'}
          </HorizontalTab>
        </HorizontalTabs>
      </HorizontalPaddingLayout>
      <Divider contrast="low" height={8} />
      {section === 'asset' && <AssetSection />}
      {section === 'introduction' && <IntroductionSection />}
      {section === 'consultation' && <ConsultationSection />}
      {section === 'landing' && <LandingSection />}
      <Divider height={8} />
      <HorizontalPaddingLayout>
        <FundActions />
      </HorizontalPaddingLayout>
    </div>
  );
};
